import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Send, Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Voglio una lettera di incarico per un nuovo cliente",
  "Aiutami con un bando della Camera di Commercio",
  "Crea una mini-app per raccogliere candidature",
  "Scrivimi un post LinkedIn per il lancio di un servizio",
  "Genera una bozza di privacy policy per il nostro sito",
  "Trasformami questi dati in un report PDF",
];

export default function Chiedi() {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const endRef = useRef(null);

  const sendMessage = async (msg) => {
    if (!msg.trim() || loading) return;
    const userMsg = { role: "user", text: msg };
    setMessages((m) => [...m, userMsg]);
    setText("");
    setLoading(true);
    try {
      const { data } = await api.post("/router/classify", { text: msg });
      const botMsg = {
        role: "bot",
        reply: data.reply,
        primary_mode: data.primary_mode,
        primary_reasoning_it: data.primary_reasoning_it,
        recipes: data.recipe_candidates || [],
        oss: data.oss_candidates || [],
        composable_hint: data.composable_hint,
      };
      setMessages((m) => [...m, botMsg]);
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore router");
    } finally {
      setLoading(false);
    }
  };

  const empty = messages.length === 0;

  return (
    <div className={empty ? "flex flex-col items-center justify-center min-h-[70vh]" : "max-w-3xl mx-auto"}>
      {empty ? (
        <div className="w-full max-w-3xl text-center">
          <div className="adr-overline mb-4 text-[#C8102E]">Adriai · Assistente</div>
          <h1 className="serif text-4xl sm:text-5xl font-semibold tracking-tight text-[#1A1A1A] leading-[1.05] mb-4">
            Cosa vuoi creare oggi?
          </h1>
          <p className="text-[#555555] text-base mb-10 max-w-xl mx-auto">
            Descrivi in italiano ciò di cui hai bisogno: ti suggerirò la ricetta giusta del catalogo e ti guiderò passo passo.
          </p>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              sendMessage(text);
            }}
            className="relative max-w-2xl mx-auto"
            data-testid="chiedi-form"
          >
            <textarea
              data-testid="chiedi-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Es. Mi serve un'analisi rapida di questo bando..."
              className="adr-input pr-14 min-h-[110px] resize-none text-base shadow-sm"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage(text);
                }
              }}
            />
            <button
              type="submit"
              disabled={loading || !text.trim()}
              className="absolute right-3 bottom-3 w-10 h-10 rounded-md text-white flex items-center justify-center disabled:opacity-40 transition-colors"
              style={{ background: "#C8102E" }}
              data-testid="chiedi-send"
              aria-label="Invia"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-w-3xl mx-auto">
            {SUGGESTIONS.map((s, i) => (
              <button
                key={i}
                onClick={() => sendMessage(s)}
                className="adr-card text-left p-4 group"
                data-testid={`suggestion-${i}`}
              >
                <div className="flex items-start gap-3">
                  <Sparkles className="w-4 h-4 text-[#0F3D6B] flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-[#1A1A1A] leading-snug">{s}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="space-y-6 pb-32">
          <div className="adr-overline">Conversazione</div>
          <h2 className="serif text-2xl font-semibold tracking-tight">Cosa vuoi creare?</h2>

          <div className="space-y-4">
            {messages.map((m, idx) => (
              <div key={idx} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                {m.role === "user" ? (
                  <div className="adr-chat-bubble-user" data-testid={`msg-user-${idx}`}>
                    {m.text}
                  </div>
                ) : (
                  <div className="adr-chat-bubble-bot space-y-3" data-testid={`msg-bot-${idx}`}>
                    {m.reply && <p className="text-sm leading-relaxed">{m.reply}</p>}
                    {m.primary_mode && (
                      <div className="text-xs">
                        <span className="adr-badge mr-2" style={{ background: "rgba(200,16,46,0.08)", color: "#C8102E" }}>
                          Modalità consigliata: {m.primary_mode.replace("_", " ")}
                        </span>
                        {m.primary_reasoning_it && <span className="text-[#555555] italic">{m.primary_reasoning_it}</span>}
                      </div>
                    )}
                    {m.recipes?.length > 0 && (
                      <div className="space-y-2 pt-1">
                        <div className="text-xs font-semibold text-[#0F3D6B]">📋 Ricette suggerite</div>
                        {m.recipes.map((r) => (
                          <button key={r.slug} onClick={() => navigate(`/catalogo/${r.slug}`)} className="w-full text-left p-3 rounded-lg border border-[#E2E8F0] hover:border-[#0F3D6B]/40 hover:bg-[#F2F5F8] transition-colors group" data-testid={`bot-recipe-${r.slug}`}>
                            <div className="flex items-start gap-3">
                              <div className="flex-1">
                                <div className="text-sm font-semibold text-[#1A1A1A] mb-1">{r.name_it}</div>
                                {r.motivazione && <p className="text-xs text-[#555555] leading-snug">{r.motivazione}</p>}
                              </div>
                              <ArrowRight className="w-4 h-4 text-[#0F3D6B] mt-1 group-hover:translate-x-0.5 transition-transform" />
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    {m.oss?.length > 0 && (
                      <div className="space-y-2 pt-1">
                        <div className="text-xs font-semibold text-[#0F3D6B]">📦 Soluzioni OSS pertinenti</div>
                        {m.oss.map((o) => (
                          <button key={o.slug} onClick={() => navigate(`/soluzioni/${o.slug}`)} className="w-full text-left p-3 rounded-lg border border-[#E2E8F0] hover:border-[#0F3D6B]/40 hover:bg-[#F2F5F8] transition-colors group" data-testid={`bot-oss-${o.slug}`}>
                            <div className="flex items-start gap-3">
                              <div className="flex-1">
                                <div className="text-sm font-semibold text-[#1A1A1A] mb-1">{o.name}</div>
                                {o.motivazione && <p className="text-xs text-[#555555] leading-snug">{o.motivazione}</p>}
                              </div>
                              <ArrowRight className="w-4 h-4 text-[#0F3D6B] mt-1 group-hover:translate-x-0.5 transition-transform" />
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    {m.composable_hint && (
                      <div className="p-3 rounded-md bg-[#F2F5F8] border border-[#E2E8F0]">
                        <div className="text-xs font-semibold text-[#0F3D6B] mb-1">🧩 Suggerimento Composable</div>
                        <p className="text-xs text-[#1A1A1A] leading-snug mb-2">{m.composable_hint}</p>
                        <button onClick={() => navigate("/componi")} className="adr-btn-outline text-xs">Vai a Composable Builder</button>
                      </div>
                    )}
                    {m.primary_mode === "APP_CUSTOM" && (
                      <button onClick={() => navigate("/costruisci")} className="adr-btn-outline text-xs">Apri App Builder Free-Form</button>
                    )}
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="adr-chat-bubble-bot">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-[#0F3D6B] rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-2 h-2 bg-[#0F3D6B] rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-2 h-2 bg-[#0F3D6B] rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          <div className="fixed bottom-0 left-0 md:left-64 right-0 bg-white border-t border-[#E2E8F0] p-4">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                sendMessage(text);
              }}
              className="max-w-3xl mx-auto relative"
            >
              <input
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Continua la conversazione..."
                className="adr-input pr-12"
                data-testid="chiedi-input-followup"
              />
              <button
                type="submit"
                disabled={loading || !text.trim()}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-md text-white flex items-center justify-center disabled:opacity-40"
                style={{ background: "#C8102E" }}
                aria-label="Invia"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
